(() => {
  if (window.ssProductAccordionMotion) return;
  window.ssProductAccordionMotion = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const selector = 'product-info .product__accordion > details';

  function install(details) {
    if (states.has(details)) return;
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .accordion__content');
    if (!summary || !content) return;
    // Keep the same margin-containment context before, during and after animation.
    details.classList.add('ss-product-accordion-motion');
    summary.addEventListener('pointerdown', () => summary.setAttribute('data-ss-pointer-focus', ''));
    summary.addEventListener('keydown', () => summary.removeAttribute('data-ss-pointer-focus'));
    summary.addEventListener('blur', () => summary.removeAttribute('data-ss-pointer-focus'));
    const state = { expanded: details.open, height: null, fade: null, saved: null };
    states.set(details, state);

    function cancelAnimations() {
      if (state.height) {
        state.height.onfinish = null;
        state.height.cancel();
        state.height = null;
      }
      if (state.fade) {
        state.fade.cancel();
        state.fade = null;
      }
    }

    function restoreStyles() {
      if (!state.saved) return;
      for (const [property, value, priority] of state.saved) {
        if (value) details.style.setProperty(property, value, priority);
        else details.style.removeProperty(property);
      }
      state.saved = null;
    }

    function finish(expanded) {
      details.open = expanded;
      state.expanded = expanded;
      cancelAnimations();
      restoreStyles();
      content.inert = false;
      summary.setAttribute('aria-expanded', String(expanded));
    }

    summary.addEventListener('click', event => {
      if (event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (reducedMotion.matches || typeof details.animate !== 'function') {
        if (state.height) finish(state.expanded);
        return;
      }
      event.preventDefault();
      const expanded = !state.expanded;
      const fromHeight = details.getBoundingClientRect().height;
      const fromOpacity = state.fade ? getComputedStyle(content).opacity : (details.open ? 1 : 0);
      cancelAnimations();
      restoreStyles();
      state.expanded = expanded;
      if (!expanded && content.contains(document.activeElement)) summary.focus({ preventScroll: true });

      try {
        // Measure only on interaction, never on initial page load.
        details.open = false;
        const closedHeight = details.getBoundingClientRect().height;
        details.open = true;
        const openHeight = details.getBoundingClientRect().height;
        state.saved = ['height', 'overflow', 'box-sizing'].map(property => [
          property, details.style.getPropertyValue(property), details.style.getPropertyPriority(property)
        ]);
        details.style.setProperty('box-sizing', 'border-box');
        details.style.setProperty('overflow', 'hidden');
        content.inert = !expanded;
        const options = {
          duration: expanded ? 220 : 160,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'both'
        };
        state.height = details.animate([
          { height: `${fromHeight}px` },
          { height: `${expanded ? openHeight : closedHeight}px` }
        ], options);
        state.fade = content.animate([
          { opacity: fromOpacity }, { opacity: expanded ? 1 : 0 }
        ], options);
        state.height.onfinish = () => finish(expanded);
      } catch (error) {
        finish(expanded);
      }
      // Keep ARIA correct after the theme's existing summary click listener.
      queueMicrotask(() => summary.setAttribute('aria-expanded', String(state.expanded)));
    });

    details.addEventListener('toggle', () => {
      if (!details.open && state.height) finish(false);
      else if (!state.height) {
        state.expanded = details.open;
        content.inert = false;
        summary.setAttribute('aria-expanded', String(details.open));
      }
    });

    state.finish = finish;
  }

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    document.querySelectorAll(selector).forEach(details => {
      const state = states.get(details);
      if (state?.height) state.finish(state.expanded);
    });
  });

  function initialize(root = document) {
    root.querySelectorAll(selector).forEach(install);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();